/**
 * A slider with a small number of named stops, which it snaps between.
 *
 * The design system has no slider and no range input anywhere, so this is a
 * new primitive — recorded as a departure in project/github.md alongside the
 * others. It is built on the native control rather than on pointer handlers so
 * that snapping, keyboard arrows, Home/End and touch all come for free; `step`
 * does the snapping, and `aria-valuetext` is what makes a screen reader say
 * "Neutral" rather than "1".
 *
 * The thumb and track are pseudo-elements, which an inline style cannot reach,
 * so this is the one component whose appearance lives in styles.css. The track
 * fill is inline, because it is computed from the value.
 */

import { useId } from 'react';
import { monoSmall } from '../theme';

export interface Stop<T extends string> {
  value: T;
  label: string;
  description: string;
}

interface Props<T extends string> {
  label: string;
  stops: ReadonlyArray<Stop<T>>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}

export function StepSlider<T extends string>({
  label,
  stops,
  value,
  onChange,
  disabled = false
}: Props<T>) {
  const id = useId();
  const index = Math.max(0, stops.findIndex((s) => s.value === value));
  const active = stops[index]!;
  const last = stops.length - 1;
  // The filled part of the track, as a percentage of its width.
  const filled = last === 0 ? 0 : (index / last) * 100;

  return (
    <div style={{ opacity: disabled ? 0.45 : 1 }}>
      <label htmlFor={id} style={{ ...monoSmall, display: 'block', marginBottom: 12 }}>
        {label}
      </label>

      <div style={{ position: 'relative', padding: '0 2px' }}>
        {/* The stops, behind the control. Purely a reading aid: the snap is
            the input's own, not these. */}
        <div
          aria-hidden="true"
          style={{ position: 'absolute', inset: '9px 2px auto', height: 2, pointerEvents: 'none' }}
        >
          {stops.map((stop, i) => (
            <span
              key={stop.value}
              style={{
                position: 'absolute',
                left: `${last === 0 ? 0 : (i / last) * 100}%`,
                transform: 'translateX(-50%)',
                width: 4,
                height: 4,
                borderRadius: 999,
                background: i <= index ? 'var(--primary)' : 'var(--border-strong)'
              }}
            />
          ))}
        </div>

        <input
          id={id}
          className="mv-slider"
          type="range"
          min={0}
          max={last}
          step={1}
          value={index}
          disabled={disabled}
          aria-valuetext={active.label}
          onChange={(e) => onChange(stops[Number(e.target.value)]!.value)}
          style={{
            // The filled half of the track. Inline because it moves with the
            // value; everything else about the track is in styles.css.
            //
            // `backgroundImage`, never the `background` shorthand: the
            // shorthand resets `background-clip` to border-box, and the clip
            // is what keeps the track a 2px line inside a 20px hit target.
            backgroundImage: `linear-gradient(to right, var(--primary) 0 ${filled}%, var(--border-strong) ${filled}% 100%)`
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, gap: 8 }}>
        {stops.map((stop, i) => (
          <span
            key={stop.value}
            style={{
              fontSize: 13,
              color: i === index ? 'var(--text-strong)' : 'var(--text-faint)',
              fontWeight: i === index ? 600 : 400,
              // The end labels sit under the end stops rather than centred on
              // them, so neither runs off the edge of the control.
              textAlign: i === 0 ? 'left' : i === last ? 'right' : 'center',
              flex: 1
            }}
          >
            {stop.label}
          </span>
        ))}
      </div>

      <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
        {active.description}
      </p>
    </div>
  );
}
